// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import ProgressiveAuthGate from '../components/ProgressiveAuthGate/ProgressiveAuthGate';
import type { ProgressiveAuthIntent } from '../config/progressiveAuthCopy';
import { progressiveAuthIntents } from '../config/progressiveAuthCopy';
import { checkWeb3Session } from '../util/effectiveAuth';
import { useWalletSessionMaybe } from './WalletSessionContext';
import { isUserAuthorized } from '../util/userHelpers';

export type ProgressiveAuthRequest = {
  intent: ProgressiveAuthIntent;
  returnTo?: string | null;
  userType?: string | null;
  onAuthenticated?: () => void;
};

type ProgressiveAuthContextValue = {
  requestAuth: (request: ProgressiveAuthRequest) => Promise<boolean>;
  closeAuth: () => void;
  isOpen: boolean;
  pendingRequest: ProgressiveAuthRequest | null;
  hasWeb3Session: boolean;
  refreshWeb3Session: () => Promise<boolean>;
  isEffectivelyAuthenticated: boolean;
};

const ProgressiveAuthContext = createContext<ProgressiveAuthContextValue | null>(null);

import { bindProgressiveAuthBridge } from '../util/authIntent';

export const ProgressiveAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const isSharetribeAuthenticated = useSelector(state => Boolean(state.auth?.isAuthenticated));
  const currentUser = useSelector(state => state.user?.currentUser);
  const walletSession = useWalletSessionMaybe();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<ProgressiveAuthRequest | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const refreshWeb3Session = useCallback(async () => {
    if (walletSession?.refreshSession) {
      return walletSession.refreshSession();
    }
    return checkWeb3Session();
  }, [walletSession]);

  const hasWeb3Session = Boolean(walletSession?.isWalletAuthenticated);

  const closeAuth = useCallback(() => {
    setIsOpen(false);
    setPendingRequest(null);
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
  }, []);

  const completeAuth = useCallback(() => {
    const callback = pendingRequest?.onAuthenticated;
    setIsOpen(false);
    setPendingRequest(null);
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
    refreshWeb3Session();
    if (callback) {
      window.setTimeout(callback, 0);
    }
  }, [pendingRequest, refreshWeb3Session]);

  const requestAuth = useCallback((request: ProgressiveAuthRequest) => {
    const sharetribeReady = isSharetribeAuthenticated && isUserAuthorized(currentUser);
    const intentConfig = progressiveAuthIntents[request.intent] || progressiveAuthIntents.general;

    return refreshWeb3Session().then(web3Ready => {
      const canProceedWithWeb3 = web3Ready && intentConfig.walletOnlyOk;

      if (sharetribeReady || canProceedWithWeb3) {
        request.onAuthenticated?.();
        return Promise.resolve(true);
      }

      return new Promise<boolean>(resolve => {
        resolverRef.current = resolve;
        setPendingRequest(request);
        setIsOpen(true);
      });
    });
  }, [currentUser, isSharetribeAuthenticated, refreshWeb3Session]);

  useEffect(() => {
    bindProgressiveAuthBridge(requestAuth);
    return () => bindProgressiveAuthBridge(null);
  }, [requestAuth]);

  const isEffectivelyAuthenticated =
    (isSharetribeAuthenticated && isUserAuthorized(currentUser)) || hasWeb3Session;

  const value = useMemo(
    () => ({
      requestAuth,
      closeAuth,
      isOpen,
      pendingRequest,
      hasWeb3Session,
      refreshWeb3Session,
      isEffectivelyAuthenticated,
    }),
    [
      requestAuth,
      closeAuth,
      isOpen,
      pendingRequest,
      hasWeb3Session,
      refreshWeb3Session,
      isEffectivelyAuthenticated,
    ]
  );

  return (
    <ProgressiveAuthContext.Provider value={value}>
      {children}
      {isOpen && pendingRequest ? (
        <ProgressiveAuthGate
          request={pendingRequest}
          onComplete={completeAuth}
          onDismiss={closeAuth}
        />
      ) : null}
    </ProgressiveAuthContext.Provider>
  );
};

export const useProgressiveAuth = () => {
  const ctx = useContext(ProgressiveAuthContext);
  if (!ctx) {
    throw new Error('useProgressiveAuth must be used within ProgressiveAuthProvider');
  }
  return ctx;
};

export default ProgressiveAuthContext;
